import React, {useEffect, useState} from 'react';
import {Container, Col, Row, Button} from 'reactstrap';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import track from 'utils/track';

import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import Webinar from 'components/Webinar';
import Video from 'components/Video';
import SalesCopy from 'components/SalesCopy';
import RegistrationModal from 'components/RegistrationModal';
import ModalExitMasterclassScreening from 'components/Modals/ModalExitMasterclassScreening';
import Footer from 'components/Elements/Footer';

const COMPLETED_MASTERCLASS_EVENT = 'Completed Watching PBCC Masterclass';

export default function ScreeningIndex({replayOverride = false, replayFull = false, ...props}) {
  const pageTitle = 'Free Masterclass: Become a Plant-Based Coach';
  const pageDescription = 'Save your spot in this free, online masterclass today.';
  const ogImage =
    'https://s3.us-west-2.amazonaws.com/cdn.foodrevolution.org/pbcc/og/pbcc-m-2026-open-graph-masteclass-fb-tw.png';
  const shareUrl = 'https://certificate.foodrevolution.org/masterclass/';
  const facebookDescription =
    'Do work that changes lives and creates a healthier world. This free, online event will teach you how to guide individuals toward a healthier, plant-based lifestyle.';
  const twitterDescription =
    'Do work that changes lives and creates a healthier world. This free, online event will teach you how to guide individuals toward a healthier, plant-based lifestyle.';
  const twitterImg =
    'https://s3.us-west-2.amazonaws.com/cdn.foodrevolution.org/pbcc/og/pbcc-m-2026-open-graph-masteclass-fb-tw.png';
  const pinterestDescription =
    'Do work that changes lives and creates a healthier world. This free, online event will teach you how to guide individuals toward a healthier, plant-based lifestyle.';
  const pinterestImg =
    'https://s3.us-west-2.amazonaws.com/cdn.foodrevolution.org/pbcc/og/pbcc-m-2026-open-graph-masteclass-pin.png';

  const {
    phases: {
      masterclass: masterclassSchedule = {
        phase: 'before'
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();

  const beforeMasterclassLaunch = masterclassSchedule.phase === 'beforeLaunch';

  const {events = []} = useUserState();
  const hasCompletedMasterclass =
    events.includes(COMPLETED_MASTERCLASS_EVENT) ||
    webinarSchedule.phase === 'replay' ||
    webinarSchedule.phase === 'after';

  const delayedReveal = (hasCompletedMasterclass && !replayOverride) || replayFull;

  const productId = 'pbcc';
  const productSku = 'pbcc';
  const productName = 'Plant-Based Coaching Certificate';
  const productSlug = 'pbcc-2026-deposit-m';
  const productValue = 100;

  const videoLabel = 'PBCC-M - Masterclass';

  const countdownTime = webinarSchedule.endOfSalesDay * 1000;
  const timerDate = new Date(countdownTime).getTime();

  useEffect(() => {
    if (hasCompletedMasterclass)
      track('Product Viewed', {
        product_id: productId,
        sku: productSku,
        name: productName,
        variant: productSlug,
        value: productValue,
        currency: 'usd'
      });
  }, [hasCompletedMasterclass]);

  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />
      <div className="page-content">
        <Header
          bgColor="purple"
          lockupWhite
          logoUrlOverride="https://cdn.foodrevolution.org/pbcc/pbcc-logo-masterclass-white.svg"
          logoAltOverride="plant-based coaching masterclass logo"
          logoWidthOverride={300}
          logoHeightOverride={59}
          shareUrl={shareUrl}
          shareImg={ogImage}
          facebookDescription={facebookDescription}
          twitterDescription={twitterDescription}
          pinterestDescription={pinterestDescription}
          pinterestImage={pinterestImg}
        />
        {beforeMasterclassLaunch ? (
          <SectionCL
            id="screening-header-cta"
            color="white"
            className="section-angle-first"
            angle={replayOverride ? 'bottom' : 'none'}
          >
            <Container>
              <Row className="text-center">
                <Col>
                  <h1 className="section-heading text-h2 mb-3">The Plant-Based Coaching Masterclass</h1>
                  <p className="balance-text">
                    Are you ready to be a LEADER in the food revolution movement? In this FREE Masterclass, you’ll
                    discover how plant-based coaching could change your life… and help you change the world.
                  </p>
                  <div className="background-light-gray rounded p-4 p-lg-5">
                    <h3 className="section-only-heading">Coming Soon!</h3>
                  </div>
                </Col>
              </Row>
            </Container>
          </SectionCL>
        ) : (
          <>
            <SectionCL
              id="screening-header-cta"
              color="white"
              className="section-angle-first"
              angle={delayedReveal || replayOverride ? 'bottom' : 'none'}
              order="first"
            >
              <Container>
                <Row className="text-center">
                  <Col>
                    <h1 className="section-heading text-h2 mb-3">The Plant-Based Coaching Masterclass</h1>
                    <p className="balance-text">
                      Are you ready to be a LEADER in the food revolution movement? In this FREE Masterclass, you’ll
                      discover how plant-based coaching could change your life… and help you change the world.
                    </p>
                  </Col>
                </Row>
                <Row className="justify-content-center mt-4">
                  <Col lg="10" className="video-col px-0 px-sm-3">
                    {replayOverride ? (
                      <Video
                        url="https://customer-ros8xhsmbkksflna.cloudflarestream.com/913e9c753dad012e1c9b1394cf2f6fcd/watch"
                        label={videoLabel}
                      />
                    ) : (
                      <Webinar noun="Masterclass" label={videoLabel} />
                    )}
                  </Col>
                </Row>
                <Row className="text-center mt-5">
                  <Col>
                    <h5 className="section-heading mb-3">Download your FREE Workbook</h5>
                    <p className="balance-text">
                      Use this workbook to get the MOST out of the Plant-Based Coaching Masterclass
                    </p>
                    <Button
                      color="cta"
                      href="https://cdn.foodrevolution.org/members/pdf/pbcc/Plant-Based%20Coaching%20Masterclass%20-%20Workbook.pdf"
                      target="_blank"
                      onClick={() => {
                        track('Button Clicked', {
                          label: 'Download My Workbook',
                          url: 'https://cdn.foodrevolution.org/members/pdf/pbcc/Plant-Based%20Coaching%20Masterclass%20-%20Workbook.pdf'
                        });
                      }}
                    >
                      Download My Workbook
                    </Button>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            {delayedReveal ? (
              <>
                <SectionCL id="" color="black" className="text-center text-white">
                  <Container>
                    <Row>
                      <Col>
                        <h2 className="section-heading mb-3">Plant-Based Coaching Certificate</h2>
                        <h3 className="m-0">
                          <i>Bring the food revolution to the world</i>
                        </h3>
                      </Col>
                    </Row>
                  </Container>
                </SectionCL>
                <SalesCopy version="masterclass" variant="default" timerDate={timerDate} />
              </>
            ) : null}
          </>
        )}
      </div>
      {replayOverride && !replayFull ? (
        <SectionCL
          id="replay-cta"
          color="purple"
          innerClass="text-center text-white section-webinar-inner-cta pb-5"
          angle="none"
        >
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading">Are you ready to be a LEADER in the food revolution movement?</h4>
                <h4 className="mt-0">Get certified as a Plant-Based Coach and be an advocate for change.</h4>
                <h4 className="mt-0 mb-5">Enrollment is open once per year and is limited to only 300 participants.</h4>
                <Button className="btn-join btn-lg" color="cta" href="/join/" target="_blank">
                  Learn More
                </Button>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      ) : (
        <>
          {!replayFull && <RegistrationModal />}
          <ModalExitMasterclassScreening />
        </>
      )}
      <div className="footer-content mt-auto">
        <Footer />
      </div>
    </div>
  );
}
